import { useEffect, useState } from 'react';
import styles from './AccuracyWidget.module.css';
import { Card, LiveBadge, Sparkline, Spinner } from '../ui';
import { getPublicAccuracy, normaliseAccuracy } from '../../api/public.js';
import { useI18n } from '../../i18n/index.jsx';

/**
 * Top Picks accuracy, shared by the marketing page and the dashboard.
 *
 * Fetches GET /api/public/accuracy and shows one of three states:
 *   ready      — enough graded picks; the percentage is published
 *   building   — some graded, but under MIN_GRADED_FOR_PUBLIC_ACCURACY, so the
 *                count is shown and the percentage withheld
 *   unavailable— nothing graded, or the call failed
 *
 * We never display an invented or under-sampled percentage — this is a public
 * performance claim.
 *
 * variant: 'hero' (dashboard) | 'card' (marketing)
 * window:  'last_30_days' (default) | 'all_time' — which bucket of the payload
 *          to read, so a page with a range control stays in step with it.
 * sparkline: set false where the page already draws the full chart, so the same
 *          series isn't plotted twice.
 */
export function AccuracyWidget({
  variant = 'hero',
  window: bucket = 'last_30_days',
  sparkline = true,
  className = '',
}) {
  const { t } = useI18n();
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    const controller = new AbortController();

    getPublicAccuracy({ signal: controller.signal })
      .then((raw) => {
        const data = normaliseAccuracy(raw, bucket);
        setState({
          status: !data ? 'unavailable' : data.meetsFloor ? 'ready' : 'building',
          data,
        });
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setState({ status: 'unavailable', data: null });
      });

    return () => controller.abort();
  }, [bucket]);

  const isHero = variant === 'hero';

  if (state.status === 'loading') {
    return (
      <Card gradient={isHero} radius={isHero ? 'xl' : 'lg'} padding="lg" className={className}>
        <div className={styles.loading}>
          <Spinner size={26} />
          <span className="muted">{t('loading')}</span>
        </div>
      </Card>
    );
  }

  // Some picks graded, but too few for the percentage to mean anything. The
  // count is shown; the figure is not. See MIN_GRADED_FOR_PUBLIC_ACCURACY.
  if (state.status === 'building') {
    return (
      <Card
        gradient={isHero}
        radius={isHero ? 'xl' : 'lg'}
        padding="lg"
        className={`${styles.widget} ${className}`}
      >
        <LiveBadge className={styles.live}>{t('accuracy_live')}</LiveBadge>
        <div className={styles.label}>{t('accuracy_building_title')}</div>
        <div className={styles.buildingCount}>
          {t('accuracy_graded_so_far', { graded: state.data.graded })}
        </div>
        <p className={styles.trackedBody}>{t('accuracy_building_body')}</p>
      </Card>
    );
  }

  // Nothing graded at all, or the endpoint is unreachable.
  if (state.status === 'unavailable') {
    return (
      <Card
        gradient={isHero}
        radius={isHero ? 'xl' : 'lg'}
        padding="lg"
        className={`${styles.widget} ${className}`}
      >
        <LiveBadge className={styles.live}>{t('accuracy_live')}</LiveBadge>
        <div className={styles.label}>{t('accuracy_tracked_title')}</div>
        <p className={styles.trackedBody}>{t('accuracy_tracked_body')}</p>
      </Card>
    );
  }

  const { pct, graded, windowDays, isAllTime, deltaPts, trend } = state.data;

  return (
    <Card
      gradient={isHero}
      radius={isHero ? 'xl' : 'lg'}
      padding="lg"
      className={`${styles.widget} ${className}`}
    >
      <LiveBadge className={styles.live}>{t('accuracy_live')}</LiveBadge>
      <div className={styles.label}>
        {isAllTime ? t('accuracy_label_all_time') : t('accuracy_label', { days: windowDays })}
      </div>
      <div className={`${styles.value} ${isHero ? styles.hero : ''} mono-num`}>{pct}%</div>

      {/* The endpoint carries no previous-period delta, so the graded count is
          the supporting figure. If a delta is added later it takes over. */}
      <div className={deltaPts !== null ? styles.trend : styles.subFigure}>
        {deltaPts !== null
          ? t('accuracy_trend', { points: deltaPts, days: windowDays, graded })
          : t('accuracy_graded', { graded })}
      </div>

      {/* Drawn only once enough days carry data — see MIN_TREND_POINTS. */}
      {sparkline && trend?.hasEnoughPoints && (
        <div className={styles.spark}>
          <Sparkline points={trend.points} height={isHero ? 90 : 80} />
        </div>
      )}
    </Card>
  );
}
